import React from "react";
import useToasts from "@ui/Toast/useToasts";
import ModalForm from "@ui/Modal/Form";
import InputV2 from "@ui/Input/InputV2";
import t from "../../../../../i18n";
import { useDescriptionInput } from "./useDescriptionInput";
import { useUnitInput } from "./useUnitInput";
import { colors } from "@ui/Theme";
import ReactSelect from "@ui/ReactSelect";
import { useEquipmentTypeInput } from "./useEquipmentTypeInput";
import { useSystemInput } from "./useSystemInput";
import { Label } from "../../Forms/Label";
import type { BaseOption } from "@ui/ReactSelect/useFormatOptionLabel";
import Rollback from "@ui/Rollback";
import type { ClassificationQuantity } from "../../../../../api/useClassificationQuantities";
import { useClassificationQuantitiesUpdate } from "../../../../../api/useClassificationQuantities";

interface P {
  onRequestClose: () => void;
  selectedQuantities: ClassificationQuantity[];
}

const mapValueToOptions = ({ name, id }: { name: string; id: string | null }): BaseOption => ({
  value: id,
  label: name,
  color: colors.raw.titanium100,
});

const QuantityModalForm = ({ onRequestClose, selectedQuantities }: P) => {
  const updateQuantities = useClassificationQuantitiesUpdate();
  const { addToast } = useToasts();

  const descriptionInput = useDescriptionInput(selectedQuantities);
  const unitInput = useUnitInput(selectedQuantities);
  const systemInput = useSystemInput(selectedQuantities);
  const equipmentTypeInput = useEquipmentTypeInput(selectedQuantities);

  const dirty =
    descriptionInput.dirty || unitInput.dirty || systemInput.dirty || equipmentTypeInput.dirty;

  const submit = React.useCallback(async () => {
    const body: Parameters<typeof updateQuantities>[0]["body"] = {
      quantityIds: selectedQuantities.map(({ id }) => id),
    };

    if (descriptionInput.dirty) body.description = descriptionInput.value;
    if (unitInput.dirty) body.unit = unitInput.value;
    if (systemInput.dirty) body.systemId = systemInput.selectedSystems?.value;
    if (equipmentTypeInput.dirty)
      body.equipmentTypeId = equipmentTypeInput.selectedEquipmentTypes?.value;

    const res = await updateQuantities({
      body,
    });

    if (res.status === 204) {
      const message = t(
        "administration.quantities.modal.action.confirm",
        selectedQuantities.length,
      );
      addToast("success", message);
      return true;
    } else if (res.status === 400) {
      addToast("error", "message");
      return false;
    } else {
      const n: typeof res = res;
      throw n;
    }
  }, [
    selectedQuantities,
    descriptionInput.dirty,
    descriptionInput.value,
    unitInput.dirty,
    unitInput.value,
    systemInput.dirty,
    systemInput.selectedSystems?.value,
    equipmentTypeInput.dirty,
    equipmentTypeInput.selectedEquipmentTypes?.value,
    updateQuantities,
    addToast,
  ]);

  return (
    <ModalForm
      onSubmit={dirty ? submit : false}
      onRequestClose={onRequestClose}
      okButtonName={t("modalForm.button.name.edit")}
    >
      <InputV2
        label={t("setup.paragraphs.quantities.table.definitions.column.description")}
        {...descriptionInput}
      />
      <InputV2
        label={t("setup.paragraphs.quantities.table.definitions.column.unit")}
        {...unitInput}
      />
      <Label>
        {t("setup.paragraphs.quantities.table.definitions.column.system")}
        {systemInput.dirty && <Rollback onClick={() => systemInput.resetValue()} />}
      </Label>
      <ReactSelect
        menuPortalTarget={window.document.body}
        placeholder={systemInput.placeholder}
        value={systemInput.selectedSystems}
        onChange={systemInput.handleSystemChanged}
        options={systemInput.systemOptions.map(mapValueToOptions)}
        onCreate={systemInput.handleSystemCreated}
        onEdit={systemInput.handleSystemEdited}
        onDelete={systemInput.handleSystemDeleted}
      />
      <Label>
        {t("setup.paragraphs.quantities.table.definitions.column.equipmentType")}
        {equipmentTypeInput.dirty && <Rollback onClick={() => equipmentTypeInput.resetValue()} />}
      </Label>
      <ReactSelect
        menuPortalTarget={window.document.body}
        placeholder={equipmentTypeInput.placeholder}
        options={equipmentTypeInput.equipmentOptions.map(mapValueToOptions)}
        value={equipmentTypeInput.selectedEquipmentTypes}
        onChange={equipmentTypeInput.handleEquipmentTypeChanged}
        onCreate={equipmentTypeInput.handleEquipmentTypeCreated}
        onEdit={equipmentTypeInput.handleEquipmentTypeEdited}
        onDelete={equipmentTypeInput.handleEquipmentTypeDeleted}
      />
    </ModalForm>
  );
};

export default React.memo(QuantityModalForm);
